import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Chip,
  Tooltip,
  
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SchoolIcon from "@mui/icons-material/School";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LayersIcon from "@mui/icons-material/Layers";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7", dark: "#92680C" },
    error: { main: "#DC2626", light: "#FEE2E2" },
    success: { main: "#059669", light: "#D1FAE5" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;


// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 860, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.30)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.40)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(({ theme }) => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    borderWidth: "1.5px",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: { bg: alpha("#DC2626", 0.08), color: "#DC2626", hbg: "#DC2626" },
  };
  const s = map[btncolor] || map.edit;
  return {
    minWidth: "unset",
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    fontFamily: "'Sora', sans-serif",
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const TestCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  padding: "18px 20px",
  marginBottom: 12,
  display: "flex",
  alignItems: "center",
  gap: 16,
  animation: `${fadeUp} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(37,99,235,0.08), 0 8px 28px rgba(0,0,0,0.06)",
    transform: "translateY(-1px)",
    borderColor: alpha("#2563EB", 0.18),
  },
  "&:last-child": { marginBottom: 0 },
}));

const LevelBadge = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: 13,
  flexShrink: 0,
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(37,99,235,0.28)",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 6px 18px rgba(37,99,235,0.38)",
    transform: "scale(1.05)",
  },
}));

const NavBtn = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
  padding: "8px 14px 8px 10px",
  borderRadius: 10,
  border: `1px solid ${alpha("#2563EB", 0.15)}`,
  background: alpha("#2563EB", 0.04),
  transition: "all 0.2s ease",
  flexShrink: 0,
  "&:hover": {
    background: "#2563EB",
    borderColor: "#2563EB",
    "& .nav-text": { color: "#fff" },
    "& .nav-icon": { color: "#fff" },
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminExamTestPage = () => {
  const navigate = useNavigate();
  const { subjectId } = useParams();

  const [tests, setTests] = useState([]);
  const [level, setLevel] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subjectName, setSubjectName] = useState("");
  const [year, setYear] = useState("");

  const fetchTests = useCallback(() => {
    fetch(`${API_URL}/admin/exam/tests/${subjectId}/`)
      .then((res) => res.json())
      .then((data) => setTests(data))
      .catch(() => alert("Error: Failed to fetch tests."));
  }, [subjectId]);

  const fetchSubject = useCallback(() => {
    fetch(`${API_URL}/admin/subject/${subjectId}/`)
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => setSubjectName(data.name))
      .catch((err) => console.error("Failed to fetch subject:", err));
  }, [subjectId]);

  useEffect(() => {
    fetchTests();
    fetchSubject();
  }, [fetchTests, fetchSubject]);

  const handleSubmit = () => {
    if (!level.trim() || !year) {
      alert("Test level and year are required.");
      return;
    }
    setLoading(true);
    const payload = { level, year };

    if (editMode) {
      fetch(`${API_URL}/admin/exam/test/${editId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then(() => {
          resetForm();
          fetchTests();
        })
        .catch(() => alert("Error: Failed to update test level."))
        .finally(() => setLoading(false));
    } else {
      fetch(`${API_URL}/admin/exam/tests/${subjectId}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((newTest) => {
          setTests([newTest, ...tests]);
          setLevel("");
        })
        .catch(() => alert("Error: Failed to add test."))
        .finally(() => setLoading(false));
    }
  };

  const startEdit = (test) => {
    setEditMode(true);
    setEditId(test.id);
    setLevel(test.level.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => resetForm();

  const resetForm = () => {
    setEditMode(false);
    setEditId(null);
    setLevel("");
  };

  const deleteTest = (testId) => {
    if (window.confirm("Are you sure you want to delete this test level?")) {
      fetch(`${API_URL}/admin/exam/test/${testId}/`, { method: "DELETE" })
        .then(() => setTests(tests.filter((t) => t.id !== testId)))
        .catch(() => alert("Error: Failed to delete test."));
    }
  };

  const navigateToQuestion = (testId) =>
    navigate(`/admin-exam-questions/${testId}`);

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                Exam Levels
              </Typography>
              {subjectName && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    mt: 0.4,
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.disabled",
                      fontFamily: "'Sora', sans-serif",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: 600,
                    }}
                  >
                    {subjectName}
                  </Typography>
                </Box>
              )}
            </Box>

            <Chip
              label={editMode ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editMode
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editMode ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editMode ? alpha("#C4922A", 0.25) : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── Form Card ── */}
          <SectionCard delay={50}>
            <CardLabel>{editMode ? "Edit Level" : "Add New Level"}</CardLabel>

            <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
              {/* Level */}
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    color: "text.disabled",
                    mb: 0.8,
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Test Level
                </Typography>
                <Box sx={{ position: "relative" }}>
                  <LayersIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 16,
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    placeholder="e.g. Beginner, Advanced, WAEC…"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
              </Box>

              {/* Year */}
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    color: "text.disabled",
                    mb: 0.8,
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Year
                </Typography>
                <Box sx={{ position: "relative" }}>
                  <CalendarTodayIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    type="number"
                    placeholder="e.g. 2024"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
              </Box>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={<CheckIcon sx={{ fontSize: "16px !important" }} />}
                disabled={loading}
                onClick={handleSubmit}
              >
                {loading ? "Saving…" : editMode ? "Update Level" : "Add Level"}
              </PrimaryBtn>
              {editMode && (
                <Button
                  onClick={cancelEdit}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Tests List ── */}
          <SectionCard delay={120}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>Test Levels</CardLabel>
              <Chip
                label={`${tests.length} level${tests.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  background: alpha("#2563EB", 0.08),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.18)}`,
                }}
              />
            </Box>

            {tests.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <LayersIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No levels added yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to create your first exam level
                </Typography>
              </Box>
            ) : (
              tests.map((test, i) => (
                <TestCard key={test.id} itemdelay={i * 55}>
                  {/* Level Number Badge */}
                  <LevelBadge onClick={() => navigateToQuestion(test.id)}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: "#fff",
                        fontFamily: "'DM Serif Display', serif",
                      }}
                    >
                      {test.level}
                    </Typography>
                  </LevelBadge>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "text.primary",
                        fontFamily: "'Sora', sans-serif",
                        lineHeight: 1.2,
                      }}
                    >
                      Level {test.level}
                    </Typography>
                    {test.year && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.6,
                          mt: 0.4,
                        }}
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: 11, color: "text.disabled" }}
                        />
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "text.disabled",
                            fontFamily: "'Sora', sans-serif",
                          }}
                        >
                          {test.year}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Navigate to questions */}
                  <NavBtn onClick={() => navigateToQuestion(test.id)}>
                    <Typography
                      className="nav-text"
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#2563EB",
                        fontFamily: "'Sora', sans-serif",
                        transition: "color 0.2s",
                      }}
                    >
                      View Questions
                    </Typography>
                    <ArrowForwardIosIcon
                      className="nav-icon"
                      sx={{
                        fontSize: 11,
                        color: "#2563EB",
                        transition: "color 0.2s",
                      }}
                    />
                  </NavBtn>

                  {/* Actions */}
                  <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Edit level" arrow>
                      <ActionBtn
                        btncolor="edit"
                        onClick={() => startEdit(test)}
                      >
                        <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                        Edit
                      </ActionBtn>
                    </Tooltip>
                    <Tooltip title="Delete level" arrow>
                      <ActionBtn
                        btncolor="delete"
                        onClick={() => deleteTest(test.id)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
                        Delete
                      </ActionBtn>
                    </Tooltip>
                  </Stack>
                </TestCard>
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminExamTestPage;
